import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  deleteCorporatePassportDocument,
  getCorporatePassportDocumentByEmployeeId,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporatePassportDocument } from '../../../../api/types/corporateEmployee';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { PassportFormDialog } from './PassportFormDialog';

/** Pasaport belgesi CRUD sub-form for the employee detail page. */
export function PassportSection({ employeeID }: { employeeID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePassportDocument | null>(null);
  const [deleting, setDeleting] = useState<CorporatePassportDocument | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['corporateEmployee', 'passports', employeeID],
    queryFn: () => getCorporatePassportDocumentByEmployeeId(employeeID),
  });
  const rows: CorporatePassportDocument[] = query.data?.corporatePassportDocument ?? [];

  const deleteMutation = useMutation({
    mutationFn: (passport: CorporatePassportDocument) =>
      deleteCorporatePassportDocument({ passportID: passport.passportID, employeeID }),
    onSuccess: () => {
      enqueueSnackbar('Pasaport belgesi silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporateEmployee', 'passports', employeeID] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1">Pasaport Belgeleri</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Pasaport Belgesi
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pasaport No</TableCell>
              <TableCell>Ülke</TableCell>
              <TableCell>Veriliş</TableCell>
              <TableCell>Son Geçerlilik</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1.5, textAlign: 'center' }}>
                    Kayıtlı pasaport belgesi bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.passportID} hover>
                <TableCell>{row.passportNumber}</TableCell>
                <TableCell>{row.passportCountry}</TableCell>
                <TableCell>{row.issueDate?.slice(0, 10) ?? '—'}</TableCell>
                <TableCell>{row.expirationDate?.slice(0, 10) ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PassportFormDialog
        open={createOpen}
        employeeID={employeeID}
        passport={null}
        onClose={() => setCreateOpen(false)}
      />
      <PassportFormDialog
        open={!!editing}
        employeeID={employeeID}
        passport={editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Pasaport belgesini sil"
        description={`"${deleting?.passportNumber ?? ''}" numaralı pasaport belgesini silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
